import React, { useRef, useState, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Sphere, Box, Torus, Cylinder, Text3D, Float, MeshDistortMaterial, MeshWobbleMaterial, Stars, Environment } from '@react-three/drei';
import { Box as MUIBox } from '@mui/material';
import { styled } from '@mui/material/styles';
import * as THREE from 'three';
import { motion } from 'framer-motion';

// Styled container for 3D scene
const Scene3DContainer = styled(MUIBox)(({ theme }) => ({
  width: '100%',
  height: '400px',
  borderRadius: '24px',
  overflow: 'hidden',
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #1a1a2e, #16213e)'
    : 'linear-gradient(135deg, #667eea, #764ba2)',
  position: 'relative',
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing'
  }
}));

// Interactive 3D Sphere with animations
const AnimatedSphere: React.FC<{ position: [number, number, number]; color: string }> = ({ position, color }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);
  const [clicked, setClicked] = useState(false);

  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.x = meshRef.current.rotation.y += delta * 0.5;
      meshRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime) * 0.1;
    }
  });

  return (
    <Sphere
      ref={meshRef}
      position={position}
      args={[0.5, 32, 32]}
      scale={clicked ? 1.5 : hovered ? 1.2 : 1}
      onClick={() => setClicked(!clicked)}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      <MeshDistortMaterial
        color={color}
        attach="material"
        distort={hovered ? 0.3 : 0.1}
        speed={2}
        roughness={0.1}
        metalness={0.8}
      />
    </Sphere>
  );
};

// Interactive 3D Cube with wobble effect
const AnimatedCube: React.FC<{ position: [number, number, number]; color: string }> = ({ position, color }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.x += delta * 0.3;
      meshRef.current.rotation.y += delta * 0.2;
      meshRef.current.position.x = position[0] + Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
    }
  });

  return (
    <Box
      ref={meshRef}
      position={position}
      args={[0.8, 0.8, 0.8]}
      scale={hovered ? 1.3 : 1}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      <MeshWobbleMaterial
        color={color}
        attach="material"
        factor={hovered ? 0.3 : 0.1}
        speed={1}
        roughness={0.2}
        metalness={0.5}
      />
    </Box>
  );
};

// Floating Torus with glow effect
const AnimatedTorus: React.FC<{ position: [number, number, number]; color: string }> = ({ position, color }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.x += delta * 0.4;
      meshRef.current.rotation.z += delta * 0.6;
      meshRef.current.position.z = position[2] + Math.cos(state.clock.elapsedTime * 0.8) * 0.2;
    }
  });

  return (
    <Float
      speed={2}
      rotationIntensity={0.5}
      floatIntensity={0.5}
    >
      <Torus
        ref={meshRef}
        position={position}
        args={[0.6, 0.2, 16, 32]}
        scale={hovered ? 1.2 : 1}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      >
        <meshStandardMaterial
          color={color}
          roughness={0.1}
          metalness={0.9}
          emissive={hovered ? color : '#000000'}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </Torus>
    </Float>
  );
};

// Technology icons in 3D space
const TechIcon3D: React.FC<{ position: [number, number, number]; text: string; color: string }> = ({ 
  position, text, color 
}) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.3;
      meshRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime + position[0]) * 0.1;
    }
  });

  return (
    <group
      ref={meshRef}
      position={position}
      scale={hovered ? 1.3 : 1}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      <Cylinder args={[0.3, 0.3, 0.1, 8]}>
        <meshStandardMaterial
          color={color}
          roughness={0.2}
          metalness={0.8}
          emissive={hovered ? color : '#000000'}
          emissiveIntensity={hovered ? 0.2 : 0}
        />
      </Cylinder>
      {/* Icon representation - simplified geometry */}
      <Box position={[0, 0, 0.1]} args={[0.2, 0.2, 0.05]}>
        <meshStandardMaterial color="#ffffff" />
      </Box>
    </group>
  );
};

// Particle system for background
const ParticleField: React.FC = () => {
  const pointsRef = useRef<THREE.Points>(null);
  const particleCount = 1000;

  const particles = React.useMemo(() => {
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 10;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 10;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 10;

      colors[i * 3] = Math.random();
      colors[i * 3 + 1] = Math.random();
      colors[i * 3 + 2] = Math.random();
    }

    return { positions, colors };
  }, []);

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 0.1) * 0.1;
      pointsRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.15) * 0.1;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={particles.positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={particleCount}
          array={particles.colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.02}
        vertexColors
        transparent
        opacity={0.6}
        sizeAttenuation
      />
    </points>
  );
};

// Camera controller for responsive movement
const CameraController: React.FC = () => {
  const { camera } = useThree();
  
  useFrame((state) => {
    camera.position.x = Math.sin(state.clock.elapsedTime * 0.1) * 0.5;
    camera.position.y = Math.cos(state.clock.elapsedTime * 0.15) * 0.2;
    camera.lookAt(0, 0, 0);
  });

  return null;
};

// Main 3D Scene Component
const Scene3D: React.FC<{ theme: 'light' | 'dark' }> = ({ theme }) => {
  return (
    <Canvas
      camera={{ position: [3, 3, 5], fov: 60 }}
      style={{ background: 'transparent' }}
      dpr={[1, 2]}
      performance={{ min: 0.5 }}
    >
      {/* Lighting */}
      <ambientLight intensity={0.4} />
      <directionalLight position={[10, 10, 5]} intensity={1} />
      <pointLight position={[-10, -10, -10]} intensity={0.5} />
      
      {/* Environment */}
      <Environment preset="city" />
      <Stars radius={100} depth={50} count={5000} factor={4} saturation={0} fade speed={1} />
      
      {/* Particle background */}
      <ParticleField />
      
      {/* Interactive 3D Objects */}
      <AnimatedSphere position={[-2, 0, 0]} color="#0066cc" />
      <AnimatedCube position={[2, 0, 0]} color="#00d4aa" />
      <AnimatedTorus position={[0, 2, 0]} color="#667eea" />
      
      {/* Technology representations */}
      <TechIcon3D position={[-1, -2, 1]} text="React" color="#61dafb" />
      <TechIcon3D position={[1, -2, 1]} text="AI" color="#ff6b6b" />
      <TechIcon3D position={[0, -2, -1]} text="Web3" color="#f7d794" />
      
      {/* Controls */}
      <OrbitControls
        enablePan={false}
        enableZoom={true}
        enableRotate={true}
        autoRotate={true}
        autoRotateSpeed={0.5}
        maxDistance={10}
        minDistance={2}
      />
      
      {/* Camera controller */}
      <CameraController />
    </Canvas>
  );
};

// Skills 3D Visualization Component
export const Skills3DVisualization: React.FC<{ theme: 'light' | 'dark' }> = ({ theme }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <Scene3DContainer>
        <Scene3D theme={theme} />
      </Scene3DContainer>
    </motion.div>
  );
};

// Hero 3D Background Component
export const Hero3DBackground: React.FC = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      setMousePosition({
        x: (event.clientX / window.innerWidth) * 2 - 1,
        y: -(event.clientY / window.innerHeight) * 2 + 1
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <MUIBox
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        opacity: 0.3
      }}
    >
      <Canvas
        camera={{ position: [0, 0, 5], fov: 75 }}
        style={{ background: 'transparent' }}
      >
        <ambientLight intensity={0.3} />
        <pointLight position={[10, 10, 10]} intensity={0.8} />
        
        <Stars radius={100} depth={50} count={5000} factor={4} saturation={0} fade speed={1} />
        
        {/* Responsive floating objects */}
        <Float speed={1} rotationIntensity={0.5} floatIntensity={0.5}>
          <Sphere position={[mousePosition.x * 2, mousePosition.y * 2, 0]} args={[0.5, 32, 32]}>
            <MeshDistortMaterial color="#0066cc" distort={0.2} speed={2} />
          </Sphere>
        </Float>
        
        <Float speed={1.2} rotationIntensity={0.3} floatIntensity={0.3}>
          <Torus position={[-mousePosition.x * 1.5, -mousePosition.y * 1.5, -2]} args={[0.8, 0.3, 16, 32]}>
            <meshStandardMaterial color="#00d4aa" metalness={0.8} roughness={0.2} />
          </Torus>
        </Float>
        
        <OrbitControls enablePan={false} enableZoom={false} enableRotate={false} />
      </Canvas>
    </MUIBox>
  );
};

// Project 3D Card Component
export const Project3DCard: React.FC<{ 
  isHovered: boolean; 
  title: string; 
  colors: string[] 
}> = ({ isHovered, title, colors }) => {
  return (
    <MUIBox
      sx={{
        width: '100%',
        height: '200px',
        borderRadius: '16px',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      <Canvas
        camera={{ position: [0, 0, 3], fov: 60 }}
        style={{ background: 'transparent' }}
      >
        <ambientLight intensity={0.6} />
        <directionalLight position={[5, 5, 5]} intensity={0.8} />
        
        <Box
          position={[0, 0, 0]}
          args={[1.5, 1, 0.1]}
          rotation={[0, isHovered ? Math.PI / 4 : 0, 0]}
        >
          <meshStandardMaterial 
            color={colors[0]} 
            metalness={0.7} 
            roughness={0.2} 
          />
        </Box>
        
        <Sphere
          position={[isHovered ? 1 : 0.5, 0, 0.2]}
          args={[0.3, 32, 32]}
          scale={isHovered ? 1.2 : 1}
        >
          <MeshWobbleMaterial 
            color={colors[1]} 
            factor={0.1} 
            speed={1} 
          />
        </Sphere>
        
        <OrbitControls 
          enablePan={false} 
          enableZoom={false} 
          enableRotate={false}
          autoRotate={isHovered}
          autoRotateSpeed={2}
        />
      </Canvas>
    </MUIBox>
  );
};

export default Skills3DVisualization;